import { useState, useCallback } from 'react'
import {
  CourseAccreditationResponse,
  CourseAccreditationType,
  InstructorWithCourses,
} from '../types'

type ModalType =
  | 'category'
  | 'template'
  | 'video'
  | 'video-preview'
  | 'show-notes'
  | 'show-notes-preview'
  | 'quiz'
  | 'quiz-preview'
  | 'title-screen-migration'
  | 'migrate-text-content'
  | 'edit-library-references'
  | 'accreditation'
  | 'instructor-create'
  | 'instructor-edit'
  | null

export function useModalState() {
  let [activeModal, setActiveModal] = useState<ModalType>(null)
  let [selectedTextContentID, setSelectedTextContentID] = useState<
    string | null
  >(null)
  let [selectedEditTemplateID, setSelectedEditTemplateID] = useState<
    string | null
  >(null)
  let [selectedEditCategoryID, setSelectedEditCategoryID] = useState<
    string | null
  >(null)
  let [selectedEditVideoID, setSelectedEditVideoID] = useState<string | null>(
    null,
  )
  let [selectedPreviewVideoID, setSelectedPreviewVideoID] = useState<
    string | null
  >(null)
  let [selectedPreviewShowNotesID, setSelectedPreviewShowNotesID] = useState<
    string | null
  >(null)
  let [selectedPreviewQuizID, setSelectedPreviewQuizID] = useState<
    string | null
  >(null)
  let [selectedAccreditation, setSelectedAccreditation] = useState<{
    accreditationType: CourseAccreditationType
    acc?: CourseAccreditationResponse
  } | null>(null)
  let [selectedEditInstructor, setSelectedEditInstructor] =
    useState<InstructorWithCourses | null>(null)
  let [createInstructorPrefillName, setCreateInstructorPrefillName] =
    useState<string>('')
  let openModal = useCallback((modal: NonNullable<ModalType>) => {
    setActiveModal(modal)
  }, [])

  let closeModal = useCallback(() => {
    setActiveModal(null)
    setSelectedTextContentID(null)
    setSelectedEditTemplateID(null)
    setSelectedEditCategoryID(null)
    setSelectedEditVideoID(null)
    setSelectedPreviewVideoID(null)
    setSelectedPreviewShowNotesID(null)
    setSelectedPreviewQuizID(null)
    setSelectedAccreditation(null)
    setSelectedEditInstructor(null)
    setCreateInstructorPrefillName('')
  }, [])

  let openTextContentModal = useCallback((textContentID: string) => {
    setSelectedTextContentID(textContentID)
    setActiveModal('migrate-text-content')
  }, [])

  let openEditTemplateModal = useCallback((templateID: string) => {
    setSelectedEditTemplateID(templateID)
    setActiveModal('template')
  }, [])

  let openEditCategoryModal = useCallback((categoryID: string) => {
    setSelectedEditCategoryID(categoryID)
    setActiveModal('category')
  }, [])

  let openEditVideoModal = useCallback((videoID: string) => {
    setSelectedEditVideoID(videoID)
    setActiveModal('video')
  }, [])

  let openVideoPreviewModal = useCallback((videoID: string) => {
    setSelectedPreviewVideoID(videoID)
    setActiveModal('video-preview')
  }, [])

  let openAddShowNotesModal = useCallback(() => {
    setActiveModal('show-notes')
  }, [])

  let openShowNotesPreviewModal = useCallback((showNotesID: string) => {
    setSelectedPreviewShowNotesID(showNotesID)
    setActiveModal('show-notes-preview')
  }, [])

  let openQuizPreviewModal = useCallback((quizID: string) => {
    setSelectedPreviewQuizID(quizID)
    setActiveModal('quiz-preview')
  }, [])

  let openAccreditationModal = useCallback(
    (
      accreditationType: CourseAccreditationType,
      acc?: CourseAccreditationResponse,
    ) => {
      setSelectedAccreditation({ acc, accreditationType })
      setActiveModal('accreditation')
    },
    [],
  )

  let openCreateInstructorModal = useCallback((prefillName: string = '') => {
    setCreateInstructorPrefillName(prefillName)
    setActiveModal('instructor-create')
  }, [])

  let openEditInstructorModal = useCallback(
    (instructor: InstructorWithCourses) => {
      setSelectedEditInstructor(instructor)
      setActiveModal('instructor-edit')
    },
    [],
  )

  return {
    activeModal,
    selectedTextContentID,
    selectedEditTemplateID,
    selectedEditCategoryID,
    selectedEditVideoID,
    selectedPreviewVideoID,
    selectedPreviewShowNotesID,
    selectedPreviewQuizID,
    selectedAccreditation,
    selectedEditInstructor,
    openModal,
    closeModal,
    openTextContentModal,
    openEditTemplateModal,
    openEditCategoryModal,
    openEditVideoModal,
    openVideoPreviewModal,
    openAddShowNotesModal,
    openShowNotesPreviewModal,
    openQuizPreviewModal,
    openAccreditationModal,
    createInstructorPrefillName,
    openCreateInstructorModal,
    openEditInstructorModal,
    // Convenience properties for checking active modal
    isCategoryModalOpen: activeModal === 'category',
    isTemplateModalOpen: activeModal === 'template',
    isVideoModalOpen: activeModal === 'video',
    isVideoPreviewModalOpen: activeModal === 'video-preview',
    isShowNotesModalOpen: activeModal === 'show-notes',
    isShowNotesPreviewModalOpen: activeModal === 'show-notes-preview',
    isQuizPreviewModalOpen: activeModal === 'quiz-preview',
    isTitleScreenMigrationModalOpen: activeModal === 'title-screen-migration',
    isMigrateTextContentModalOpen: activeModal === 'migrate-text-content',
    isEditLibraryReferencesModalOpen: activeModal === 'edit-library-references',
    isAccreditationModalOpen: activeModal === 'accreditation',
    isCreateInstructorModalOpen: activeModal === 'instructor-create',
    isEditInstructorModalOpen: activeModal === 'instructor-edit',
  }
}
